// `common/components/text-read-more` — a CSS line clamp plus a "Read more" link that appears
// only when the text actually overflows it. Used by the candidate profile twice: on every job
// description (6 lines, as HTML) and on the ATS note (5 lines, as plain text).
import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactElement } from 'react'

export interface TextReadMoreProps {
  text: string
  /** `-webkit-line-clamp`; upstream passes it as a number and interpolates it into a string. */
  lineLimit: number
  /**
   * ⚠️ TRUE MEANS THE TEXT IS SERVER-AUTHORED MARKUP, rendered through `ng-bind-html` upstream
   * and `dangerouslySetInnerHTML` here. The job description passes true — its competency chips
   * ARE the markup. The ATS note passes false: that text is whatever a recruiter typed into
   * their ATS, so it must stay escaped.
   */
  displayAsHtml?: boolean
}

export function TextReadMore({ text, lineLimit, displayAsHtml = false }: TextReadMoreProps): ReactElement {
  const container = useRef<HTMLDivElement>(null)
  const [isInitialOverLimit, setIsInitialOverLimit] = useState(false)
  const [readMore, setReadMore] = useState(false)

  useLayoutEffect(() => {
    const element = container.current
    if (element === null) return
    // Upstream measures inside a 100ms `$timeout` — it needs a digest to have painted the
    // clamped box first. A layout effect runs after exactly that paint, so the delay has
    // nothing left to wait for.
    setIsInitialOverLimit(element.offsetHeight < element.scrollHeight)
  }, [text, lineLimit])

  // ⚠️ THE CLAMP IS ON THE STATIC `class`, NOT ONLY ON `ng-class`, and the two disagree. The
  // template writes `class="text-read-more-container read-more-line-limit"` and then an
  // `ng-class` that adds the SAME class conditionally. So the first render is always clamped
  // whatever the condition says, and the box only ever opens because ngClass removes a class
  // it believes it owns. This expression is what that adds up to, written once.
  const clamped = !readMore

  const style: CSSProperties = { WebkitLineClamp: lineLimit }

  return (
    <>
      <div
        ref={container}
        className={`text-read-more-container${clamped ? ' read-more-line-limit' : ''}`}
        style={style}
      >
        {displayAsHtml ? (
          <div className="text-read-more-content" dangerouslySetInnerHTML={{ __html: text }} />
        ) : (
          <div className="text-read-more-content">{text}</div>
        )}
      </div>
      {!isInitialOverLimit ? null : (
        <div className="text-read-more-actions cursor-pointer">
          {/* "Show less", not "Read less" — upstream has a TODO asking the same question, and
              the answer a port gives is the string that is on screen today. */}
          <div
            role="button"
            tabIndex={0}
            onClick={() => {
              setReadMore((previous) => !previous)
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') setReadMore((previous) => !previous)
            }}
          >
            {readMore ? 'Show less' : 'Read more'}
          </div>
        </div>
      )}
    </>
  )
}
